import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Divider,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  TextField,
  Backdrop,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
  Paper,
} from "@mui/material";
import { ArrowForward, Send as SendIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  getAnswer,
  getCarrierByBusinessName,
} from "../../redux/action/carrier";
import Header from "../header/Header";
import { setTheme } from "../../redux/reducers/themeSlice";
import { getMovesByPhoneNumber } from "../../redux/action/move-service";
import StepProgress from "../stepsProgress/stepsProgress";

const MainScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading data...");
  const [carrierEmail, setCarrierEmail] = useState("");
  const { businessName } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const [phoneNumber, setPhoneNumber] = useState<any>(null);
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("");
  const [carrierCanonicalId, setCarrierCanonicalId] = useState("");
  const [color, setColor] = useState("#4A90E2");
  const isValidColor = (color: string): boolean => {
    const colorRegex =
      /^#([0-9A-Fa-f]{3}){1,2}$|^(rgb|rgba|hsl|hsla)\([^\)]*\)$/;
    return colorRegex.test(color);
  };

  useEffect(() => {
    if (businessName) {
      dispatch<any>(getCarrierByBusinessName(businessName))
        .then(unwrapResult)
        .then((res: any) => {
          setCarrierEmail(res.email);
          setCarrierCanonicalId(res.canonical_id);
          if (res.color_hex && isValidColor(res.color_hex)) {
            setColor(res.color_hex);
            dispatch(
              setTheme({
                primaryColor: res.color_hex,
                secondaryColor: "#A8E6FF",
              })
            );
          }
        })
        .catch((err: any) => console.error("Failed to fetch carrier:", err))
        .finally(() => setIsLoading(false));
    }
  }, [businessName, dispatch]);

  useEffect(() => {
    if (businessName) localStorage.setItem("businessName", businessName);
    if (carrierEmail) localStorage.setItem("email", carrierEmail);
  }, [businessName, carrierEmail]);

  const [conversation, setConversation] = useState<any>([]);

  const handleAskQuestion = async () => {
    if (!question || !carrierEmail) return;
    setIsLoading(true);
    setLoadingMessage("Analyzing your question...");
    try {
      const result = await dispatch<any>(
        getAnswer({ email: carrierEmail, question })
      );
      const response = unwrapResult(result);
      setAnswer(response.answer);
      setConversation((prev: any) => [
        ...prev,
        { customerQuery: question, AiResponse: response.answer },
      ]);
      setQuestion("");
    } catch (error) {
      console.error(error);
      setAnswer("Sorry, I couldn't process your question. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleServiceClick = (service: string) => {
    if (service === "Moving Services+" && businessName) {
      navigate(`/${businessName}/${carrierCanonicalId}/choose-service`);
    } else if (service === "Previous Customer") {
      navigate(`/${businessName}/${carrierCanonicalId}/consumer-login`);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = e.target.value.replace(/\D/g, "");
    if (formattedValue.length <= 10) {
      setPhoneNumber(formattedValue);
      setPhoneError("");
    }
    if (formattedValue && formattedValue.length < 10)
      return setMessage("Phone number must be 10 digits");
    setMessage("");
  };

  const validatePhoneNumber = () => {
    if (phoneNumber.length !== 10) {
      setPhoneError("Phone number must be 10 digits");
      return false;
    }
    return true;
  };

  const handleFindMove = async () => {
    if (!validatePhoneNumber()) return;
    setIsLoading(true);
    setLoadingMessage("Searching for your moves...");

    try {
      await dispatch<any>(getMovesByPhoneNumber(phoneNumber))
        .then(unwrapResult)
        .then((res: any) => {
          const { last_step, canonical_id } = res;
          const pathMap: Record<number, string> = {
            1: "choose-service",
            2: `delivery-details/${canonical_id}`,
            3: `apartment-info/${canonical_id}`,
            4: `bulky-items/${canonical_id}`,
            5: `insurance/${canonical_id}`,
            6: `user-details/${canonical_id}`,
            7: `storage/${canonical_id}`,
            8: `guidelines/${canonical_id}`,
            9: `inventory/${canonical_id}`,
            10: `move-price/${canonical_id}`,
            11: `move-summary/${canonical_id}`,
          };
          if (pathMap[last_step])
            navigate(`/${businessName}/${pathMap[last_step]}`);
          else setMessage("No Pending Move Found");
        });
    } catch {
      setMessage("No move found for this phone number");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundColor: "#f7f8fa",
          minHeight: "93vh",
          py: 4,
          px: isMobile ? 2 : 6,
          overflowY: "auto",
        }}
      >
        {isLoading && (
          <Backdrop
            open
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0,0,0,0.3)",
              flexDirection: "column",
            }}
          >
            <CircularProgress size={50} color="inherit" />
            <Typography variant="h6" mt={2}>
              {loadingMessage}
            </Typography>
          </Backdrop>
        )}
        <StepProgress currentStep={1} />

        <Container maxWidth="md">
          {/* Continue Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: "16px",
              backgroundColor: `${color}20`, // adds 12% opacity tint
              border: `1px solid ${color}40`, // subtle border to match tint
              transition: "background 0.3s ease",
            }}
          >
            <Typography fontWeight={700} fontSize={18} mb={1}>
              Continue where you left off
            </Typography>
            <Typography fontSize={14} color="text.secondary" mb={3}>
              Enter your phone number to find your existing booking.
            </Typography>

            <Box
              display="flex"
              gap={2}
              flexDirection={isMobile ? "column" : "row"}
            >
              <TextField
                fullWidth
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                error={!!phoneError}
                helperText={phoneError}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>+1</Typography>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: `${color}20`,
                    "& fieldset": {
                      borderColor: "transparent", // remove default gray outline
                    },
                    "&:hover fieldset": {
                      borderColor: `${color}50`, // soft border on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: color,
                    },
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handleFindMove}
                disabled={isLoading || phoneNumber?.length !== 10}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: color,
                  "&:hover": {
                    backgroundColor: color,
                    opacity: 0.9,
                  },
                }}
              >
                {isLoading ? "Searching..." : "Find Move"}
              </Button>
            </Box>

            {message && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {message}
              </Alert>
            )}
          </Paper>

          {/* Service Section */}
          <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: "16px" }}>
            <Typography fontWeight={700} fontSize={18} mb={1}>
              Select Service Type
            </Typography>
            <Typography mb={1} color={"#000000"} fontSize={12}>
              Choose the option that best fits your needs:
            </Typography>

            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              {[
                {
                  title: "Moving Services+",
                  icon: "🚚",
                  description:
                    "Book a new move with our full-service moving options",
                },
                {
                  title: "Previous Customer",
                  icon: "⏱️",
                  description:
                    "Welcome back! Continue with your existing account",
                },
              ].map((service, index) => (
                <Paper
                  key={index}
                  onClick={() => handleServiceClick(service.title)}
                  sx={{
                    p: 3,
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderRadius: "16px",
                    backgroundColor: "#fff",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0px 6px 12px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    <Typography fontSize={36}>{service.icon}</Typography>
                    <Typography color="primary" fontSize={20}>
                      →
                    </Typography>
                  </Box>

                  <Typography
                    fontWeight={600}
                    fontSize={16}
                    color="text.primary"
                    mt={3}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    fontSize={14}
                    color="text.secondary"
                    mt={0.5}
                    sx={{ lineHeight: 1.5 }}
                  >
                    {service.description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Paper>

          {/* Ask Question / Chat Section */}
          <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: "16px" }}>
            <Typography fontWeight={700} fontSize={18} mb={3}>
              Have a Question?
            </Typography>

            {conversation.length > 0 && (
              <Box
                ref={containerRef}
                sx={{
                  height: 250,
                  overflowY: "auto",
                  p: 2,
                  mb: 3,
                  backgroundColor: "#f9fafb",
                  borderRadius: "10px",
                }}
              >
                {conversation.map((item: any, index: any) => (
                  <Box
                    key={index}
                    ref={
                      index === conversation.length - 1 ? lastMessageRef : null
                    }
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography fontWeight={600} color="text.secondary" mb={1}>
                      You:
                    </Typography>
                    <Typography sx={{ mb: 2, pl: 1 }}>
                      {item.customerQuery}
                    </Typography>
                    <Typography fontWeight={600} color="text.secondary" mb={1}>
                      Response:
                    </Typography>
                    <Typography sx={{ pl: 1 }}>{item.AiResponse}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Type your question here..."
              InputProps={{
                endAdornment: (
                  <Tooltip title="Send question">
                    <IconButton
                      onClick={handleAskQuestion}
                      disabled={!question || isLoading}
                      sx={{
                        position: "absolute",
                        right: 10,
                        bottom: 10,
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Paper>

          {/* Footer */}
          <Box textAlign="center" mt={4}>
            <Typography variant="body2">
              Existing Booking?{" "}
              <Typography
                component="span"
                color="primary"
                fontWeight={600}
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    `/${businessName}/${carrierCanonicalId}/consumer-login`
                  )
                }
              >
                Login Here
              </Typography>
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default MainScreen;
